import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useEvidence } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { LinkManager } from '@/components/shared/LinkManager';
import { EvidenceCreateDialog } from '@/components/evidence/EvidenceCreateDialog';

export default function EvidencePage() {
  const { organization } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvidenceId, setSelectedEvidenceId] = useState<string | null>(null);
  const [newEvidence, setNewEvidence] = useState({
    name: '',
    type: 'document',
    notes: '',
  });
  
  const { evidence, isLoading, createEvidence, deleteEvidence } = useEvidence(organization?.id);
  
  const selectedEvidence = evidence?.find(e => e.id === selectedEvidenceId);

  const handleSubmit = async () => {
    if (!newEvidence.name) {
      toast.error('Please enter a name');
      return;
    }
    
    if (!organization?.id) {
      toast.error('No organization found');
      return;
    }

    createEvidence({
      ...newEvidence,
      org_id: organization.id,
    });
    setNewEvidence({ name: '', type: 'document', notes: '' });
    setDialogOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this evidence?')) {
      deleteEvidence(id);
    }
  };

  if (isLoading) {
    return <div>Loading evidence...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Evidence</h1>
          <p className="text-muted-foreground">Manage compliance evidence and documentation</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Evidence
        </Button>
      </div>

      <div className="space-y-4">
        {!evidence || evidence.length === 0 ? (
          <p className="text-muted-foreground">No evidence yet. Add your first evidence document.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {evidence.map((item) => (
              <Card 
                key={item.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedEvidenceId(item.id)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-primary" />
                      <div>
                        <CardTitle className="text-lg">{item.name}</CardTitle>
                        <CardDescription>{item.type}</CardDescription>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                {item.notes && (
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{item.notes}</p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      <EvidenceCreateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSubmit}
      />

      <Sheet open={!!selectedEvidenceId} onOpenChange={() => setSelectedEvidenceId(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {selectedEvidence && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {selectedEvidence.name}
                </SheetTitle>
              </SheetHeader>
              
              <div className="mt-6 space-y-6">
                <div>
                  <h3 className="text-sm font-medium mb-2">Type</h3>
                  <p className="text-sm text-muted-foreground">{selectedEvidence.type}</p>
                </div>
                
                {selectedEvidence.notes && (
                  <div>
                    <h3 className="text-sm font-medium mb-2">Notes</h3>
                    <p className="text-sm text-muted-foreground">{selectedEvidence.notes}</p>
                  </div>
                )}
                
                <Separator />
                
                <LinkManager
                  entityType="evidence"
                  entityId={selectedEvidence.id}
                  entityName={selectedEvidence.name}
                  allowedTypes={['asset', 'system', 'risk', 'control', 'supplier', 'incident']}
                  showHeader={true}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
